import type { Document, SourceId } from '@/core/document'
import { ExportUnsupportedError } from '@/core/export/errors'
import { planExport } from '@/core/export/planExport'
import {
  ExportMessageType,
  type ExportRequestSource,
  type ExportWorkerResponse,
  type QualityRequest,
  type QualityResponse,
  type QualityFullEncodedResponse,
  type QualityWindowEncodedResponse,
} from '@/core/export/protocol'
import type { ExportOptions } from '@/core/export/types'
import type { Point } from '@/core/geometry'
import type { SourceImage } from '@/core/image'
import { probeMaxCanvasArea } from '@/core/render/canvasLimits'

interface PendingRequest {
  resolve: (response: QualityResponse) => void
  reject: (error: Error) => void
}

export interface QualityWorkerSession {
  navigator: ImageBitmap
  outputSize: { width: number; height: number }
  encodeWindow: (point: Point, quality: number) => Promise<QualityWindowEncodedResponse>
  encodeFull: (quality: number) => Promise<QualityFullEncodedResponse>
  dispose: () => void
}

function closeResponseBitmaps(response: ExportWorkerResponse): void {
  if (response.type === ExportMessageType.QualityRendered) response.navigator.close()
  if (response.type === ExportMessageType.QualityWindowEncoded) {
    response.original.close()
    response.jpeg.close()
    response.difference.close()
  }
}

export async function startQualityWorkerSession(
  sources: ReadonlyMap<SourceId, SourceImage>,
  document: Document,
  options: ExportOptions,
): Promise<QualityWorkerSession> {
  if (typeof OffscreenCanvas === 'undefined') throw new ExportUnsupportedError()
  if (typeof Worker === 'undefined') throw new ExportUnsupportedError('Web Workers')
  if (typeof createImageBitmap === 'undefined') throw new ExportUnsupportedError('createImageBitmap')
  if (sources.size === 0) throw new Error('Quality view requires at least one source image')
  const maxCanvasArea = await probeMaxCanvasArea()

  const requestSources: ExportRequestSource[] = []
  try {
    for (const [id, source] of sources) {
      requestSources.push({
        id,
        bitmap: await createImageBitmap(source.bitmap),
        width: source.width,
        height: source.height,
      })
    }
  } catch (error) {
    requestSources.forEach((source) => source.bitmap.close())
    throw error
  }

  let worker: Worker
  try {
    worker = new Worker(new URL('../../core/export/export.worker.ts', import.meta.url), { type: 'module' })
  } catch (error) {
    requestSources.forEach((source) => source.bitmap.close())
    throw error
  }
  const pending = new Map<number, PendingRequest>()
  let nextRequestId = 1
  let disposed = false

  const failAll = (error: Error) => {
    for (const request of pending.values()) request.reject(error)
    pending.clear()
  }

  const send = (request: QualityRequest, transfer: Transferable[] = []): Promise<QualityResponse> => (
    new Promise((resolve, reject) => {
      if (disposed) {
        reject(new Error('Quality worker session is closed'))
        return
      }
      if (pending.has(request.requestId)) {
        reject(new Error(`Quality worker request id ${request.requestId} is already pending`))
        return
      }
      pending.set(request.requestId, { resolve, reject })
      try {
        worker.postMessage(request, transfer)
      } catch (error) {
        pending.delete(request.requestId)
        reject(error instanceof Error ? error : new Error('Could not send a quality worker request'))
      }
    })
  )

  worker.onmessage = (event: MessageEvent<ExportWorkerResponse>) => {
    const response = event.data
    if (disposed) {
      closeResponseBitmaps(response)
      if (response.type === ExportMessageType.QualityDisposed) worker.terminate()
      return
    }
    if (!('requestId' in response) || response.requestId === undefined) {
      closeResponseBitmaps(response)
      failAll(new Error(`Quality worker received response "${response.type}" without a request id`))
      worker.terminate()
      disposed = true
      return
    }
    const request = pending.get(response.requestId)
    if (!request) {
      closeResponseBitmaps(response)
      failAll(new Error(`Quality worker returned unknown request id ${response.requestId}`))
      worker.terminate()
      disposed = true
      return
    }
    pending.delete(response.requestId)
    if (response.type === ExportMessageType.Failure) request.reject(new Error(response.message))
    else request.resolve(response)
  }
  worker.onerror = (event) => {
    failAll(new Error(event.message || 'Quality worker failed'))
    worker.terminate()
    disposed = true
  }

  const renderRequestId = nextRequestId
  nextRequestId += 1
  let rendered
  try {
    rendered = await send({
      type: ExportMessageType.RenderForQuality,
      requestId: renderRequestId,
      sources: requestSources,
      document,
      plan: planExport(document, sources, options, maxCanvasArea),
      options,
      maxCanvasArea,
    }, requestSources.map((source) => source.bitmap))
  } catch (error) {
    requestSources.forEach((source) => source.bitmap.close())
    worker.terminate()
    disposed = true
    throw error
  }
  if (rendered.type !== ExportMessageType.QualityRendered) {
    closeResponseBitmaps(rendered)
    worker.terminate()
    disposed = true
    throw new Error(`Quality worker expected a rendered response, received "${rendered.type}"`)
  }

  const navigator = rendered.navigator
  return {
    navigator,
    outputSize: rendered.outputSize,
    encodeWindow: async (point, quality) => {
      const requestId = nextRequestId
      nextRequestId += 1
      const response = await send({
        type: ExportMessageType.EncodeQualityWindow,
        requestId,
        point,
        quality,
      })
      if (response.type !== ExportMessageType.QualityWindowEncoded) {
        closeResponseBitmaps(response)
        throw new Error(`Quality worker expected a window response, received "${response.type}"`)
      }
      return response
    },
    encodeFull: async (quality) => {
      const requestId = nextRequestId
      nextRequestId += 1
      const response = await send({
        type: ExportMessageType.EncodeQualityFull,
        requestId,
        quality,
      })
      if (response.type !== ExportMessageType.QualityFullEncoded) {
        closeResponseBitmaps(response)
        throw new Error(`Quality worker expected a full response, received "${response.type}"`)
      }
      return response
    },
    dispose: () => {
      if (disposed) return
      navigator.close()
      disposed = true
      failAll(new Error('Quality worker session was closed'))
      const requestId = nextRequestId
      worker.postMessage({ type: ExportMessageType.DisposeQuality, requestId })
    },
  }
}
